import { useState } from "react";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { format, parse } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";

/**
 * DatePickerEs — calendario en español que devuelve fechas en formato
 * "YYYY-MM-DD" (lo que espera el backend). `value` también es string ISO.
 *
 * Props:
 *  - value: "YYYY-MM-DD" | ""
 *  - onChange: (iso: string) => void
 *  - placeholder: string
 *  - allowClear: boolean
 *  - testId: string
 */
export default function DatePickerEs({
  value = "",
  onChange,
  placeholder = "Seleccione fecha",
  allowClear = true,
  disabled = false,
  fromYear = 1940,
  toYear = new Date().getFullYear() + 5,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const parsed = value ? parse(value, "yyyy-MM-dd", new Date()) : null;
  const isValid = parsed && !isNaN(parsed.getTime());

  const handleSelect = (d) => {
    if (!d) {
      onChange?.("");
    } else {
      onChange?.(format(d, "yyyy-MM-dd"));
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          type="button"
          disabled={disabled}
          className={`w-full justify-start font-normal ${!isValid ? "text-zinc-500" : "text-zinc-900"}`}
          data-testid={testId}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {isValid ? format(parsed, "d MMMM yyyy", { locale: es }) : placeholder}
          {isValid && allowClear && !disabled && (
            <span
              onClick={(e) => { e.stopPropagation(); onChange?.(""); }}
              className="ml-auto text-zinc-400 hover:text-red-600 p-0.5"
              aria-label="Limpiar fecha"
              role="button"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          locale={es}
          captionLayout="dropdown-buttons"
          fromYear={fromYear}
          toYear={toYear}
          defaultMonth={isValid ? parsed : new Date(toYear - 25, 0)}
          selected={isValid ? parsed : undefined}
          onSelect={handleSelect}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
